import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { countDescendants } from './utils/catalogSelection';

import type { CspCatalogTreeNode } from '@/shared/types/userStandard';

export interface CartItem {
  // children are stripped to keep cart state light; scalar fields are retained for rendering, and the
  // submit path reads only `node.id`, `withDescendants`, and `excludedIds`.
  node: CspCatalogTreeNode;
  // true = whole-branch root (server expands its subtree); false = a single node (leaf or search hit).
  withDescendants: boolean;
  // Subtree roots unchecked under a whole-branch root (tri-state exclusion). Each excluded id drops that
  // node and everything beneath it. Always empty when `withDescendants` is false.
  excludedIds: string[];
  // Total descendants of the node at add time (the section's size); used for display only.
  descendantCount: number;
}

export interface StandardsCartState {
  items: Record<string, CartItem>;
}

const initialState: StandardsCartState = { items: {} };

/** Build a cart item from a catalog node, computing the descendant count before stripping children. */
export function buildCartItem(
  node: CspCatalogTreeNode,
  withDescendants: boolean,
  excludedIds: string[] = []
): CartItem {
  return {
    node: { ...node, children: [] },
    withDescendants,
    excludedIds: withDescendants ? excludedIds : [],
    descendantCount: withDescendants ? countDescendants(node) : 0,
  };
}

const cartSlice = createSlice({
  name: 'standardsCart',
  initialState,
  reducers: {
    addCartItems: (state, action: PayloadAction<CartItem[]>) => {
      for (const item of action.payload) state.items[item.node.id] = item;
    },
    removeCartItems: (state, action: PayloadAction<string[]>) => {
      for (const id of action.payload) delete state.items[id];
    },
    setItemExclusions: (state, action: PayloadAction<{ id: string; excludedIds: string[] }>) => {
      const item = state.items[action.payload.id];
      if (item) item.excludedIds = action.payload.excludedIds;
    },
    clearCart: (state) => {
      state.items = {};
    },
  },
});

export const { addCartItems, removeCartItems, setItemExclusions, clearCart } = cartSlice.actions;
export default cartSlice.reducer;
